// CalendarComponent.js
import React, { useState, useMemo, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Paper, Box } from "@mui/material";
import PropTypes from "prop-types";
import {
  IconButton,
  Typography,
  Stack,
  TextField,
  DialogContent,
  DialogTitle,
  Dialog,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import "moment/locale/ar";
const localizer = momentLocalizer(moment);

const CalendarComponent = ({ events = [], onSelectSlot, applicantMeetings = [] }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  // --- MISSING FUNCTION 2: Handle Date Picker Input ---
  const [extraDialogOpen, setExtraDialogOpen] = useState(false);
  const [extraMeetings, setExtraMeetings] = useState([]);
  const [extraMeetingsDate, setExtraMeetingsDate] = useState(null);
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  useEffect(() => {
    if (isArabic) {
      moment.locale("ar");
    } else {
      moment.locale("en");
    }
  }, [isArabic]);

  const handleNavigate = (action) => {
    let newDate;
    if (action === "PREV") {
      newDate = moment(currentDate).subtract(1, "month").toDate();
    } else if (action === "NEXT") {
      newDate = moment(currentDate).add(1, "month").toDate();
    } else {
      newDate = new Date(); // Default to Today
    }
    setCurrentDate(newDate);
  };

  const handleDateChange = (event) => {
    const dateValue = event.target.value;
    if (dateValue) {
      setCurrentDate(new Date(dateValue));
    }
  };

  const mappedEvents = useMemo(() => {
    return applicantMeetings.map((meeting) => {
      const start = new Date(meeting.dateTime);
      const end = new Date(meeting.dateTime); // adjust if you want duration
      return {
        id: meeting.id,
        title: moment(start).format("hh:mm A"), // shows time like 11:10
        start,
        end,
        allDay: false,
      };
    });
  }, [applicantMeetings]);

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <IconButton
          onClick={() => handleNavigate("PREV")}
          aria-label="previous day"
          sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <TextField
          label={t("admin.talentAcquisition.meetings.selectDate")}
          type="date"
          size="small"
          // Ensure this matches the state format
          value={moment(currentDate).locale("en").format("YYYY-MM-DD")}
          onChange={handleDateChange}
          sx={{ width: 220 }}
          InputLabelProps={{ shrink: true }}
        />

        <IconButton
          onClick={() => handleNavigate("NEXT")}
          aria-label="next day"
          sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Calendar
          localizer={localizer}
          culture={isArabic ? "ar" : "en"} 
          rtl={isArabic} // This flips the layout for Arabic
          events={mappedEvents}
          startAccessor="start"
          endAccessor="end"
          selectable // allows selecting empty slots
          onSelectSlot={onSelectSlot} // callback when user clicks a slot
          // onSelectSlot={(slotInfo, e) => onSelectSlot(slotInfo, e?.target)}
          onShowMore={(events, date) => {
            setExtraMeetings(events); // all events on that day
            setExtraMeetingsDate(date); // store the date
            setExtraDialogOpen(true); // open the dialog
          }}
          views={["month"]}
          defaultView="month"
          style={{ height: 500 }}
          toolbar={false}
          date={currentDate}
        />
      </Paper>
      <Dialog open={extraDialogOpen} onClose={() => setExtraDialogOpen(false)}>
        <DialogTitle>Meetings on {moment(extraMeetingsDate).format("MMM Do, YYYY")}</DialogTitle>
        <DialogContent>
          {extraMeetings.map((m) => (
            <Typography key={m.id}>{moment(m.start).format("hh:mm A")}</Typography>
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

CalendarComponent.propTypes = {
  events: PropTypes.array,
  onSelectSlot: PropTypes.func,
  applicantMeetings: PropTypes.object,
};

export default CalendarComponent;
